import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

// Activity Clusters definition with sequence numbers
export const ACTIVITY_CLUSTERS = {
  Crawling: [
    // Phase 1: Initial domain validation and crawling
    { task: 'pre_klazify_domain', sequence: 1 },
    { task: 'crawl_the_domain', sequence: 2 },
    { task: 'decide_if_sw_company_task', sequence: 3 },

    // Phase 2: Data collection
    { task: 'collect_sourcescrub_data_task', sequence: 4 },
    { task: 'identify_all_images', sequence: 5 },
    { task: 'extract_emails_task', sequence: 6 },
    { task: 'extract_phone_numbers_task', sequence: 7 },

    // Phase 3: API enrichment
    { task: 'xovi_api_call_for_company_task', sequence: 8 },
    { task: 'klazify_api_call', sequence: 9 },

    // Phase 4: Analysis
    { task: 'average_keywords_task', sequence: 10 },
    { task: 'calculate_company_size', sequence: 11 },
    { task: 'summarize_website_task', sequence: 12 },
    { task: 'classify_all_pages_task', sequence: 13 },

    // Phase 5: Social analysis
    { task: 'analyze_social_network_post_proxycurl', sequence: 14 },
    { task: 'analyze_social_network', sequence: 15 },
    { task: 'analyze_social_network_task_with_decorator', sequence: 16 },
    { task: 'social_network_analysis_callback_task', sequence: 17 },

    // Phase 6: Wrap up
    { task: 'overall_crawl', sequence: 18 },
    { task: 'overall crawl', sequence: 18 }, // Alternative naming
    { task: 'release_lock_task', sequence: 19 },
  ],
  'DA Preparation': [
    // Data analysis and KPI calculations
    { task: 'analyze_company_techstack_task', sequence: 1 },
    { task: 'analyze_customer_references_task', sequence: 2 },
    { task: 'classify_type_of_product_task', sequence: 3 },
    { task: 'segment_customer_task', sequence: 4 },
    { task: 'llm_segment_customer_task', sequence: 5 },
    { task: 'openai_classification', sequence: 6 },
    { task: 'mongo_embedding_creation_for_single_company_task', sequence: 7 },
    { task: 'calculate_company_kpis', sequence: 8 },
    { task: 'calculate_company_kpis_task', sequence: 9 },
  ],
  Housekeeping: [
    // Maintenance and scheduled tasks
    { task: 'buy_and_build_list_scheduled_prep_task', sequence: 1 },
    { task: 'buy_and_build_list_prep_task', sequence: 2 }, // Alternative naming
    { task: 'check_openregistry_data_task', sequence: 3 },
    { task: 'add_technology_finding_text', sequence: 4 },
    { task: 'trigger_similar_companies_from_sourcescrub_task', sequence: 5 },
  ],
} as const;

// Helper function to extract task names from cluster definition
const extractTaskNames = (clusters: typeof ACTIVITY_CLUSTERS) => {
  const tasks: string[] = [];
  Object.values(clusters).forEach(clusterTasks => {
    clusterTasks.forEach(taskDef => {
      if (!tasks.includes(taskDef.task)) {
        tasks.push(taskDef.task);
      }
    });
  });
  return tasks;
};

// Task sequence definition (for backward compatibility)
export const TASK_SEQUENCE = extractTaskNames(ACTIVITY_CLUSTERS);

export type ClusterName = keyof typeof ACTIVITY_CLUSTERS;
export type TaskDefinition = { task: string; sequence: number };
export type TaskName = string;
export type TaskStatus = 'pending' | 'started' | 'completed' | 'failed' | 'finished' | 'skipped';

interface Notification {
  message: string;
  timestamp: Date;
  read: boolean;
  companyDomain?: string;
  companyId?: number;
  taskName?: TaskName | string;
  taskStatus?: TaskStatus;
}

interface CompanyProgress {
  domain: string;
  companyId?: number;
  tasks: Map<
    string,
    {
      status: TaskStatus;
      timestamp: Date;
    }
  >;
  lastUpdate: Date;
  overallProgress: number;
}

interface NotificationsContextType {
  notifications: Notification[];
  companyProgressMap: Map<string, CompanyProgress>;
  addNotification: (notification: Notification) => void;
  markAllAsRead: () => void;
  getCompanyProgress: (domain: string) => CompanyProgress | undefined;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

// Helper function to parse notification message
const parseNotificationMessage = (
  message: string
): {
  status?: TaskStatus;
  taskName?: string;
  companyDomain?: string;
  companyId?: number;
} => {
  // Parse messages like: (status: 'completed', task_name: 'collect_sourcescrub_data_task', company_domain: 'https://www.emagia.com', company_id: 309148)
  const statusMatch = message.match(/status:\s*'([^']+)'/);
  const taskMatch = message.match(/task_name:\s*'([^']+)'/);
  const domainMatch = message.match(/company_domain:\s*'([^']+)'/);
  const idMatch = message.match(/company_id:\s*(\d+)/);

  return {
    status: statusMatch?.[1] as TaskStatus,
    taskName: taskMatch?.[1],
    companyDomain: domainMatch?.[1],
    companyId: idMatch ? parseInt(idMatch[1]) : undefined,
  };
};

export const NotificationsProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const companyProgressMap = useMemo(() => {
    const map = new Map<string, CompanyProgress>();

    notifications.forEach(notification => {
      // Parse the notification if needed
      if (!notification.companyDomain && notification.message) {
        const parsed = parseNotificationMessage(notification.message);
        notification.companyDomain = parsed.companyDomain;
        notification.companyId = parsed.companyId;
        notification.taskName = parsed.taskName;
        notification.taskStatus = parsed.status;
      }

      if (notification.companyDomain && notification.taskName) {
        const domain = notification.companyDomain;

        if (!map.has(domain)) {
          map.set(domain, {
            domain,
            companyId: notification.companyId,
            tasks: new Map(),
            lastUpdate: notification.timestamp,
            overallProgress: 0,
          });
        }

        const progress = map.get(domain)!;
        progress.tasks.set(notification.taskName, {
          status: notification.taskStatus || 'pending',
          timestamp: notification.timestamp,
        });
        progress.lastUpdate = notification.timestamp;

        // Calculate overall progress
        const completedTasks = Array.from(progress.tasks.values()).filter(
          t => t.status === 'completed' || t.status === 'finished'
        ).length;
        progress.overallProgress = (completedTasks / TASK_SEQUENCE.length) * 100;
      }
    });

    return map;
  }, [notifications]);

  const addNotification = (notification: Notification) => {
    // Parse the message if it's a raw status message
    if (notification.message && !notification.companyDomain) {
      const parsed = parseNotificationMessage(notification.message);
      notification = {
        ...notification,
        ...parsed,
      };
    }

    setNotifications(prev => [...prev, notification]);
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notification => ({ ...notification, read: true })));
  };

  const getCompanyProgress = (domain: string) => {
    return companyProgressMap.get(domain);
  };

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        companyProgressMap,
        addNotification,
        markAllAsRead,
        getCompanyProgress,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};
